import { Head, Link, router } from "@inertiajs/react"
import { AlertTriangle, FileText, Upload } from "lucide-react"
import { useRef, useState } from "react"

import { RiderStatusBadge } from "@/components/products/fleet/badges"
import {
  EMPTY,
  formatDate,
  formatNumber,
} from "@/components/products/fleet/format"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { formatMoney } from "@/lib/money-format"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface RiderRow {
  id: number
  rider_number: string
  lessor: { id: number; name: string } | null
  effective_on: string | null
  expiration_on: string | null
  monthly_rate_cents: number | null
  status: string
  expiring_soon: boolean
  car_count: number | null
  cars: number
  requal_conflicts: number
  allowable_miles: number | null
  mileage_penalty: string | null
  return_conditions: string | null
}

interface PendingContract {
  id: number
  filename: string | null
  created_at: string
  rider_number: string | null
  lessor_name: string | null
  effective_on: string | null
  expiration_on: string | null
  monthly_rate_cents: number | null
  allowable_miles: number | null
  mileage_penalty: string | null
  return_conditions: string | null
  car_count: number | null
  car_numbers: string[]
  confidence_notes: string | null
  match_status: string | null
  will_create: boolean
  note: string | null
}

interface Props {
  riders: RiderRow[]
  pending_contracts: PendingContract[]
  can_edit: boolean
  filters: {
    lessor_id: string | null
    expiring_within_months: string | null
  }
  filter_options: {
    lessors: { id: number; name: string }[]
  }
}

const ANY = "any"

const EXPIRY_WINDOWS = [
  { value: "3", label: "Expiring within 3 months" },
  { value: "6", label: "Expiring within 6 months" },
  { value: "12", label: "Expiring within 12 months" },
]

export default function RidersIndex({
  riders,
  pending_contracts,
  can_edit,
  filters,
  filter_options,
}: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Lessors", href: routes.lessorsPath() },
    { title: "Riders", href: routes.ridersPath() },
  ]

  function applyFilters(overrides: Partial<Props["filters"]>) {
    const next = { ...filters, ...overrides }
    const params: Record<string, string> = {}
    for (const [key, value] of Object.entries(next)) {
      if (value && value !== ANY) params[key] = String(value)
    }
    router.get(routes.ridersPath(), params, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  const conflicts = riders.reduce((sum, r) => sum + r.requal_conflicts, 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Riders" />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Riders</h1>
            <p className="text-muted-foreground text-sm">
              {riders.length} {riders.length === 1 ? "rider" : "riders"},
              soonest to expire first.
              {conflicts > 0 &&
                ` ${conflicts} ${conflicts === 1 ? "car is" : "cars are"} scheduled to be requalified after their lease ends.`}
            </p>
          </div>
          {can_edit && <UploadContract />}
        </div>

        {can_edit && pending_contracts.length > 0 && (
          <PendingContracts contracts={pending_contracts} />
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={filters.lessor_id ?? ANY}
            onValueChange={(value) => applyFilters({ lessor_id: value })}
          >
            <SelectTrigger
              className="h-9 w-[12rem] text-sm"
              aria-label="Filter by lessor"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All lessors</SelectItem>
              {filter_options.lessors.map((lessor) => (
                <SelectItem key={lessor.id} value={String(lessor.id)}>
                  {lessor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.expiring_within_months ?? ANY}
            onValueChange={(value) =>
              applyFilters({ expiring_within_months: value })
            }
          >
            <SelectTrigger
              className="h-9 w-[14rem] text-sm"
              aria-label="Filter by expiry"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any expiry date</SelectItem>
              {EXPIRY_WINDOWS.map((window) => (
                <SelectItem key={window.value} value={window.value}>
                  {window.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rider</TableHead>
                <TableHead>Lessor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Rate / mo</TableHead>
                <TableHead className="text-right">Allowable mi.</TableHead>
                <TableHead className="text-right">Cars</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {riders.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-muted-foreground py-8 text-sm"
                  >
                    No riders match these filters. Clear a filter to see the
                    full lease structure.
                  </TableCell>
                </TableRow>
              ) : (
                riders.map((rider) => (
                  <TableRow key={rider.id}>
                    <TableCell className="font-medium">
                      {rider.rider_number}
                    </TableCell>
                    <TableCell className="text-sm">
                      {rider.lessor ? (
                        <Link
                          href={routes.lessorPath(rider.lessor.id)}
                          className="hover:underline"
                        >
                          {rider.lessor.name}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <RiderStatusBadge
                        status={rider.status}
                        expiringSoon={rider.expiring_soon}
                      />
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(rider.effective_on)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-sm",
                        rider.expiring_soon &&
                          "font-medium text-amber-600 dark:text-amber-400",
                      )}
                    >
                      {formatDate(rider.expiration_on)}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {formatMoney(rider.monthly_rate_cents)}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      <AllowableMiles rider={rider} />
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      <div className="flex items-center justify-end gap-1.5">
                        {rider.cars}
                        {rider.requal_conflicts > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-0.5 text-red-600 dark:text-red-400">
                                <AlertTriangle
                                  className="size-3.5"
                                  aria-hidden="true"
                                />
                                {rider.requal_conflicts}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              {rider.requal_conflicts} of these cars come due
                              for requalification after this rider expires.
                              Testing them buys compliance the lease will never
                              use.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppLayout>
  )
}

// The allowable-mileage cap plus, on hover, the overage penalty and return
// conditions extracted from the lease contract — the terms operators used to
// hand-key from a PDF.
function AllowableMiles({ rider }: { rider: RiderRow }) {
  if (rider.allowable_miles == null) {
    return <span className="text-muted-foreground">{EMPTY}</span>
  }

  const hasTerms = Boolean(rider.mileage_penalty || rider.return_conditions)
  const miles = `${formatNumber(rider.allowable_miles)} mi`

  if (!hasTerms) return <span>{miles}</span>

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted underline-offset-2">
          {miles}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs space-y-1 text-left">
        {rider.mileage_penalty && (
          <p>
            <span className="font-medium">Overage:</span>{" "}
            {rider.mileage_penalty}
          </p>
        )}
        {rider.return_conditions && (
          <p>
            <span className="font-medium">Return:</span>{" "}
            {rider.return_conditions}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

function UploadContract() {
  const routes = useOrgRoutes()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return

    const data = new FormData()
    data.append("file", file)
    setBusy(true)
    router.post(routes.uploadRiderContractPath(), data, {
      preserveScroll: true,
      onFinish: () => {
        setBusy(false)
        if (inputRef.current) inputRef.current.value = ""
      },
    })
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={onFile}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="size-4" aria-hidden="true" />
        {busy ? "Uploading…" : "Upload lease contract"}
      </Button>
    </div>
  )
}

// The review queue for contracts the pipeline has read. Nothing here has
// touched a Rider yet — Confirm is what writes the terms, Discard drops the
// reading. Same operator-in-the-loop split the inbox uses for work orders.
function PendingContracts({ contracts }: { contracts: PendingContract[] }) {
  const routes = useOrgRoutes()
  const [busyId, setBusyId] = useState<number | null>(null)

  function confirm(contract: PendingContract) {
    setBusyId(contract.id)
    router.post(
      routes.confirmRiderContractPath(contract.id),
      {},
      { preserveScroll: true, onFinish: () => setBusyId(null) },
    )
  }

  function reject(contract: PendingContract) {
    setBusyId(contract.id)
    router.post(
      routes.rejectRiderContractPath(contract.id),
      {},
      { preserveScroll: true, onFinish: () => setBusyId(null) },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="size-4" aria-hidden="true" />
          Lease contracts to review ({contracts.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {contracts.map((contract) => (
          <div key={contract.id} className="rounded-md border p-3 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium">
                  {contract.rider_number ?? "Unnamed rider"}
                  {contract.lessor_name && (
                    <span className="text-muted-foreground font-normal">
                      {" "}
                      · {contract.lessor_name}
                    </span>
                  )}
                </p>
                <p className="text-muted-foreground text-xs">
                  {contract.filename ?? "contract"}
                  {contract.note && ` — ${contract.note}`}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={busyId === contract.id}
                  onClick={() => confirm(contract)}
                >
                  {contract.will_create ? "Create rider" : "Apply terms"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busyId === contract.id}
                  onClick={() => reject(contract)}
                >
                  Discard
                </Button>
              </div>
            </div>

            <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
              <Term
                label="Effective"
                value={formatDate(contract.effective_on)}
              />
              <Term
                label="Expires"
                value={formatDate(contract.expiration_on)}
              />
              <Term
                label="Rate / mo"
                value={formatMoney(contract.monthly_rate_cents)}
              />
              <Term
                label="Allowable miles"
                value={
                  contract.allowable_miles == null
                    ? EMPTY
                    : `${formatNumber(contract.allowable_miles)} mi`
                }
              />
              <Term
                label="Cars"
                value={
                  contract.car_count == null
                    ? EMPTY
                    : formatNumber(contract.car_count)
                }
              />
              <Term
                label="Overage penalty"
                value={contract.mileage_penalty ?? EMPTY}
              />
            </dl>

            {contract.return_conditions && (
              <p className="text-muted-foreground mt-2 text-xs">
                <span className="font-medium">Return conditions:</span>{" "}
                {contract.return_conditions}
              </p>
            )}
            {contract.confidence_notes &&
              contract.confidence_notes !== "all clear" && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  {contract.confidence_notes}
                </p>
              )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function Term({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  )
}
